import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import Banner from "./Banner";
import MapContent from "./MapContent";
import TripContent from "./Trip/TripContent";
import "./styles/styles.css";

import fetchFlag from "../../utils/featureFlags";
import { fetchPaxBalance } from "../../utils/paxHelpers";
import { webServiceCall } from "../../services/pantherService";
import { getClientInfo } from "../login/loginService";

import SideMenu from "./Dashboard/SideMenu";
import StripePopup from "./Dashboard/StripePopup";
import PaxDetailsPopup from "./Dashboard/PaxDetailsPopup";
import AddFundsOptionsPopup from "./Dashboard/AddFundsOptionsPopup";
import CustomAmountPopup from "./Dashboard/CustomAmountPopup";

export default function DashboardPage() {
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [paxOpen, setPaxOpen] = useState(false);
  const [selectedPax, setSelectedPax] = useState(null);
  const [clientData, setClientData] = useState(() => {
    const storedClientData = localStorage.getItem("clientData");

    return storedClientData ? JSON.parse(storedClientData) : null;
  });
  const [topupOptions, setTopupOptions] = useState([]);
  const [canShowStripe, setCanShowStripe] = useState(false);
  const [allowTopUp, setAllowTopUp] = useState(false);
  const [addFundsOpen, setAddFundsOpen] = useState(false);
  const [paxBalance, setPaxBalance] = useState(null);
  const [canShowBalance, setCanShowBalance] = useState(null);
  const [stripePopup, setStripePopup] = useState(null);
  const [customAmountPopup, setCustomAmountPopup] = useState(false);
  const [customAmount, setCustomAmount] = useState("");

  const [editedPhones, setEditedPhones] = useState({
    telephone: "",
    altTelephone: "",
    mobile: "",
  });

  const [originalPhones, setOriginalPhones] = useState({
    telephone: "",
    altTelephone: "",
    mobile: "",
  });

  const [isSaving, setIsSaving] = useState(false);

  const { guid } = useParams();
  const location = useLocation();

  const handleOpenPax = async (pax) => {
    setSelectedPax(pax);
    setPaxOpen(true);
    setPaxBalance(null);

    const phones = {
      telephone: pax.Telephone || "",
      altTelephone: pax.AltTelephone || "",
      mobile: pax.Mobile || "",
    };

    setEditedPhones(phones);
    setOriginalPhones(phones);

    const balance = await fetchPaxBalance(guid, pax.PaxId);
    setPaxBalance(balance);
  };

  const openAddFunds = () => setAddFundsOpen(true);

  useEffect(() => {
  const loadClient = async () => {
    if (!guid) return;

    const latestClientData = await getClientInfo(guid);

    if (latestClientData) {
      setClientData(latestClientData);
    }
  };

  loadClient();
}, [guid]);

  useEffect(() => {
    const refreshClientInfo = async () => {
      if (!guid) return;

      const latestClientData = await getClientInfo(guid);

      if (latestClientData) {
        setClientData(latestClientData);
      }
    };

    refreshClientInfo();
    const loadFlagsAndOptions = async () => {
      try {
        const options = await fetchFlag(guid, "GetStripeTopupOptions");
        setTopupOptions(options);

        const stripeEnabled = await fetchFlag(guid, "CheckStripeTopupEnabled");
        setCanShowStripe(stripeEnabled);

        const allowTopupFlag = await fetchFlag(guid, "CheckAllowTopup");
        setAllowTopUp(allowTopupFlag);

        const showBalanceFlag = await fetchFlag(guid, "CheckShowBalance");
        setCanShowBalance(showBalanceFlag);
      } catch (err) {
        console.error(err);
        setTopupOptions([]);
        setCanShowStripe(false);
        setAllowTopUp(false);
        setCanShowBalance(false);
      }
    };

    loadFlagsAndOptions();
  }, [guid]);

  useEffect(() => {
    const stripeMessage = location.state?.stripeMessage;

    if (stripeMessage) {
      setStripePopup(stripeMessage);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location]);

  const startStripeTopUp = async (amount, pax) => {
    const url = `${window.location.origin}${window.location.pathname}`;

    const checkoutUrl = await webServiceCall("CreateStripeCheckout", {
      URL: url,
      GUId: guid,
      PaxId: pax.PaxId,
      Amount: amount,
    });

    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  const savePaxChanges = async () => {
    if (!selectedPax) return;

    setIsSaving(true);

    try {
      await webServiceCall("UpdatePax", {
        GUId: guid,
        PaxId: selectedPax.PaxId,
        Telephone: editedPhones.telephone,
        AltTelephone: editedPhones.altTelephone,
        Mobile: editedPhones.mobile,
      });

      setOriginalPhones(editedPhones);

      const updatedPax = {
        ...selectedPax,
        ...editedPhones,
      };

      setSelectedPax(updatedPax);
      localStorage.setItem("paxData", JSON.stringify(updatedPax));
    } catch (err) {
      console.error(err);
      alert("Failed to update Pax.");
    }

    setIsSaving(false);
  };

  return (
    <div className="app">
      <Banner
        onToggleMenu={() => setMenuOpen(!menuOpen)}
        onOpenPax={handleOpenPax}
        canShowStripe={canShowStripe}
        allowTopUp={allowTopUp}
        canShowBalance={canShowBalance}
        onAddFunds={openAddFunds}
      />

      <main className="content" style={{ display: "flex" }}>
        <MapContent trip={selectedTrip} />
        <TripContent onSelectTrip={setSelectedTrip} />
      </main>

      {menuOpen && (
        <SideMenu clientData={clientData} onClose={() => setMenuOpen(false)} />
      )}

      {stripePopup && (
        <StripePopup
          message={stripePopup}
          onClose={() => setStripePopup(null)}
        />
      )}

      {paxOpen && selectedPax && (
        <PaxDetailsPopup
          selectedPax={selectedPax}
          editedPhones={editedPhones}
          setEditedPhones={setEditedPhones}
          originalPhones={originalPhones}
          savePaxChanges={savePaxChanges}
          isSaving={isSaving}
          paxBalance={paxBalance}
          allowTopUp={allowTopUp}
          canShowStripe={canShowStripe}
          canShowBalance={canShowBalance}
          onAddFunds={openAddFunds}
          onClose={() => setPaxOpen(false)}
          clientData={clientData}
        />
      )}

      {addFundsOpen && selectedPax && (
        <AddFundsOptionsPopup
          topupOptions={topupOptions}
          selectedPax={selectedPax}
          startStripeTopUp={startStripeTopUp}
          onOther={() => {
            setAddFundsOpen(false);
            setCustomAmountPopup(true);
          }}
          onClose={() => setAddFundsOpen(false)}
        />
      )}

      {customAmountPopup && (
        <CustomAmountPopup
          customAmount={customAmount}
          setCustomAmount={setCustomAmount}
          onClose={() => setCustomAmountPopup(false)}
          onSubmit={() => {
            const numValue = parseFloat(customAmount);

            if (!isNaN(numValue) && numValue > 0) {
              setCustomAmountPopup(false);
              startStripeTopUp(numValue, selectedPax);
            } else {
              alert("Please enter a valid amount.");
            }
          }}
        />
      )}
    </div>
  );
}
